import * as React from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Fragment, SFC } from "react";
import { Entry, Link as BaseLink, useDocs, useMenus } from "docz";
import { useWindowSize, useSessionStorage } from "react-use";
import styled, { css } from "styled-components";

import { breakpoints } from "@styles/responsive";
import { mainContext } from "../Main";
import { TOPBAR_LINKS, IconLink } from "../Topbar";

interface WrapperProps {
  opened: boolean;
  mobile: boolean;
}

const toggle = (p: WrapperProps) => {
  return !p.opened && p.mobile ? "-100%" : "0";
};

const SidebarWrapper = styled.div<WrapperProps>`
  width: 250px;
  height: calc(100vh - 60px);
  min-width: 250px;
  padding: 40px 20px 40px 30px;
  border-right: 1px solid ${p => p.theme.colors.grayLight};
  background: #f8f8f9;
  transition: transform 0.2s, background 0.3s;
  transform: translateX(${toggle});
  position: sticky;
  top: 60px;
  max-height: calc(100vh - 60px);
  overflow: auto;

  &.no-top-bar {
    top: 0;
  }

  ${p =>
    p.mobile &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      overflow: auto;
      z-index: 9999;
      padding: 30px;
      padding-left: 30px;
      height: 100vh;
      max-height: 100vh;
    `};
`;

const Wrapper = styled.div`
  position: -webkit-sticky;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${p =>
    p.theme.mq({
      position: ["relative", "relative", "sticky", "sticky"],
      top: ["0px", "0px", "0px", "0px"]
    })};
`;

const Link = styled(BaseLink)`
  font-size: 14px;
  padding: 2px 0;
  color: rgb(76, 85, 90);
  line-height: 1.5em;
  transition: 0.4s;

  &,
  &:visited {
    color: ${p => p.theme.colors.grayDark};
  }

  &.active,
  &:hover {
    color: ${p => p.theme.colors.primary};
  }
`;

const SmallLink = styled(BaseLink)`
  font-size: 14px;
  padding: 0 0 5px 10px;
  transition: 0.4s;

  &,
  &:visited {
    color: ${p => p.theme.colors.grayDark};
  }

  &.active {
    color: ${p => p.theme.colors.primary};
  }

  &:hover {
    color: ${p => p.theme.colors.primary};
  }
`;

const Submenu = styled.div`
  display: flex;
  flex-direction: column;
  margin: 5px 0;
  font-size: 14px;
  border-left: 1px solid #eee;
  line-height: 1.5em;
`;

const MenuGroup = styled.h2`
  font-size: 15px;
  text-transform: uppercase;
  color: rgb(0, 0, 0);
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
  font-weight: normal;

  &:first-child {
    margin-top: 0;
  }
`;

interface OpenProps {
  opened: boolean;
}

const ToggleBackground = styled.div`
  content: "";
  display: ${(p: OpenProps) => (!p.opened ? "none" : "block")};
  position: fixed;
  background-color: rgba(0, 0, 0, 0.4);
  width: 100vw;
  height: 100vh;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  cursor: pointer;
  z-index: 98;
`;

interface MenuProps {
  doc: Entry;
  active: boolean;
  onClick: React.MouseEventHandler;
  hash?: string;
}

const Menu: SFC<MenuProps> = ({ doc, active, hash, onClick }) => {
  const headings = doc.headings.filter(
    heading => heading.depth > 1 && heading.depth < 3
  );
  const hashValue = hash && hash.substr(1);

  return (
    <Fragment>
      <Link to={doc.route} onClick={onClick} className={active ? "active" : ""}>
        {doc.name}
      </Link>
      {active && headings.length > 0 && (
        <Submenu>
          {headings.map(heading => (
            <SmallLink
              key={heading.slug}
              to={`${doc.route}#${heading.slug}`}
              onClick={onClick}
              className={
                hashValue === encodeURIComponent(heading.slug) ? "active" : ""
              }
            >
              {heading.value}
            </SmallLink>
          ))}
        </Submenu>
      )}
    </Fragment>
  );
};

interface TopbarMenuProps {
  onClick: React.MouseEventHandler;
}

const TopbarMenu: React.SFC<TopbarMenuProps> = ({ onClick }) => {
  return (
    <React.Fragment>
      {TOPBAR_LINKS.map(({ id, children, ...props }) => {
        const Component = props.to ? Link : IconLink;
        return (
          <Component key={id} {...props} onClick={onClick}>
            {children}
          </Component>
        );
      })}
    </React.Fragment>
  );
};

interface SidebarProps {
  menu: string;
  pathname?: string;
  mobile?: boolean;
  hash?: string;
}

export const Sidebar: SFC<SidebarProps> = ({ pathname, hash, mobile }) => {
  const docs = useDocs();
  const { width } = useWindowSize();
  const { showing, setShowing } = useContext(mainContext);
  const scrollRef = useRef(null);
  const [scrollTop, setscrollTop] = useSessionStorage("sidebar-scrolltop", 0);
  const [translateY, setTranslateY] = useState(scrollTop);
  let menus = useMenus();

  menus = (menus || []).filter(menu => {
    if (!menu.menu) return false;
    return true;
  });
  const isDesktop = width > breakpoints.tablet;

  const toggle = useCallback(() => {
    setShowing((s: any) => !s);
  }, []);

  const handleSidebarToggle = (ev: React.SyntheticEvent<any>) => {
    if (isDesktop) {
      scrollRef.current && setscrollTop(scrollRef.current.scrollTop);
      return;
    }
    toggle && toggle();
  };

  useEffect(() => {
    setTranslateY(0);
    scrollRef.current && scrollRef.current.scrollTo(0, scrollTop || 0);
  }, []);

  return (
    <React.Fragment>
      <SidebarWrapper ref={scrollRef} opened={showing} mobile={Boolean(mobile)}>
        <Wrapper style={{ transform: `translateY(-${translateY}px)` }}>
          {mobile && <TopbarMenu onClick={handleSidebarToggle} />}
          {menus &&
            menus.map(({ id, name, menu }) => {
              if (!menu) return null;

              return (
                <React.Fragment key={id}>
                  <MenuGroup>{name}</MenuGroup>
                  {menu.map(item => {
                    const doc =
                      docs &&
                      docs.find(
                        doc => doc.name === item.name && doc.menu === name
                      );
                    if (!doc) return null;

                    const active =
                      doc.route === "/"
                        ? pathname === "/"
                        : pathname &&
                          pathname.split("?")[0].replace(/\/$/, "") ===
                            doc.route.split("?")[0];
                    return (
                      <Menu
                        key={doc.id}
                        doc={doc}
                        active={Boolean(active)}
                        hash={hash}
                        onClick={handleSidebarToggle}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
        </Wrapper>
      </SidebarWrapper>
      {!isDesktop && (
        <ToggleBackground opened={showing} onClick={handleSidebarToggle} />
      )}
    </React.Fragment>
  );
};
